import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, KeyboardAvoidingView, Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import { useAuth } from '../contexts/AuthContext';
import { TEST_WORKER_URL } from '../runtime/mobile/testWorkerClient';
import { kv } from '../runtime/mobile/kv';
import { colors, radius, spacing, typography } from '../config/theme';
import { Pictogram, StickerBackdrop } from './brand';
import { screen } from './ui';

/* global window */
const SpeechRec = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;

const GREETING = {
  chat: 'Привет! Я помощник «Якобы». Спроси что угодно про работу — подскажу.',
  product: 'Опиши, что нужно клиенту — подберу товар из того, что есть в наличии.',
  analytics: 'Аналитика магазина за 30 дней: смены, часы, задачи, опоздания, погода. Спрашивай.',
};
const SUGGESTIONS = {
  chat: [
    'Клиент просит кофе без горечи — что предложить?',
    'Как быстро успокоить недовольного клиента?',
    'Чем отличается капучино от флэт-уайта?',
  ],
  product: [
    'Нужен крепкий вкус с ментолом до 1500 ₽',
    'Что-то фруктовое, некрепкое, для новичка',
    'Подскажи замену — закончился любимый вкус',
  ],
  analytics: [
    'На что обратить внимание за месяц?',
    'Кто лучше и хуже закрывает задачи?',
    'Есть связь между погодой и сменами?',
    'В какие дни недели проседаем?',
  ],
};
const LABELS = { chat: 'Помощник', product: 'Товар', analytics: 'Аналитика' };

export default function AiScreen() {
  const { user, isOwner } = useAuth();
  const [mode, setMode] = useState('chat');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [locked, setLocked] = useState(false);
  const scroller = useRef(null);
  const storeKey = user ? `ai:hist:${user.uid}:${mode}` : null;

  // Restore / persist the conversation so it survives leaving the screen.
  useEffect(() => {
    setError(null); setLocked(false); setMessages([]);
    if (!storeKey) return;
    kv.get(storeKey).then((raw) => {
      if (raw) { try { setMessages(JSON.parse(raw) || []); } catch { /* ignore */ } }
    }).catch(() => {});
  }, [storeKey]);
  useEffect(() => {
    if (storeKey) kv.set(storeKey, JSON.stringify(messages.slice(-40))).catch(() => {});
  }, [messages, storeKey]);
  useEffect(() => { const t = setTimeout(() => scroller.current?.scrollToEnd?.({ animated: true }), 60); return () => clearTimeout(t); }, [messages, busy]);

  const ask = useCallback(async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy || locked) return;
    const next = [...messages, { role: 'user', content: q }];
    setMessages(next);
    setInput('');
    setBusy(true);
    setError(null);
    try {
      const token = await user.getIdToken();
      const r = await fetch(`${TEST_WORKER_URL}/ai/chat`, {
        method: 'POST',
        headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
        body: JSON.stringify({ mode, messages: next.map((m) => ({ role: m.role, content: m.content })) }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (j.error === 'AI_DAILY_LIMIT') setLocked(true);
        setError(j.message || 'Помощник недоступен. Попробуйте позже.');
        setMessages(messages);
        setInput(q);
        return;
      }
      setMessages([...next, { role: 'assistant', content: j.text }]);
    } catch {
      setError('Нет связи с помощником.');
      setMessages(messages);
      setInput(q);
    } finally {
      setBusy(false);
    }
  }, [input, busy, locked, messages, mode, user]);

  const clear = () => { setMessages([]); setError(null); if (storeKey) kv.set(storeKey, '[]').catch(() => {}); };

  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const dictate = () => {
    if (!SpeechRec) return;
    if (listening) { try { recRef.current?.stop(); } catch { /* ignore */ } return; }
    try {
      const rec = new SpeechRec();
      rec.lang = 'ru-RU'; rec.interimResults = true; rec.continuous = false;
      rec.onresult = (e) => {
        const t = Array.from(e.results).map((r) => r[0].transcript).join(' ');
        setInput(t);
      };
      rec.onend = () => setListening(false);
      rec.onerror = () => setListening(false);
      recRef.current = rec;
      setListening(true);
      rec.start();
    } catch { setListening(false); }
  };

  return (
    <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={screen.root}>
      <StickerBackdrop seed="assistant" opacity={0.11} />
      <View style={styles.topbar}>
        <View style={styles.segment}>
          {(isOwner ? ['chat', 'product', 'analytics'] : ['chat', 'product']).map((k) => (
            <Pressable key={k} onPress={() => setMode(k)} style={[styles.seg, mode === k && styles.segOn]}>
              <Text style={[styles.segText, mode === k && styles.segTextOn]}>{LABELS[k]}</Text>
            </Pressable>
          ))}
        </View>
        {messages.length > 0 ? <Pressable onPress={clear} hitSlop={8}><Text style={styles.clear}>Очистить</Text></Pressable> : null}
      </View>

      <ScrollView ref={scroller} contentContainerStyle={styles.feed} keyboardShouldPersistTaps="handled">
        <View style={styles.hello}>
          <Pictogram name={mode === 'analytics' ? 'chart' : mode === 'product' ? 'shop' : 'chat'} size={22} color={colors.primary} />
          <Text style={styles.helloText}>{GREETING[mode]}</Text>
        </View>

        {messages.length === 0 && !locked && (
          <View style={styles.chips}>
            {(SUGGESTIONS[mode] || []).map((s) => (
              <Pressable key={s} style={styles.chip} onPress={() => ask(s)}>
                <Text style={styles.chipText}>{s}</Text>
              </Pressable>
            ))}
          </View>
        )}

        {messages.map((m, i) => (
          <View
            key={i}
            style={[styles.bubble, m.role === 'user' ? styles.bubbleMine : styles.bubbleAi]}
          >
            <Text style={[styles.msg, m.role === 'user' && styles.msgMine]}>{m.content}</Text>
          </View>
        ))}
        {busy && (
          <View style={[styles.bubble, styles.bubbleAi, styles.thinking]}>
            <ActivityIndicator size="small" color={colors.textMuted} />
            <Text style={styles.thinkingText}>думаю…</Text>
          </View>
        )}
        {error ? <Text style={styles.err}>{error}</Text> : null}
        {locked ? <Text style={styles.err}>Помощник исчерпал бесплатный лимит на сегодня. Вернётся завтра.</Text> : null}
      </ScrollView>

      <View style={styles.composer}>
        <TextInput
          style={styles.input}
          value={input}
          onChangeText={setInput}
          placeholder={locked ? 'Лимит на сегодня' : 'Спросите помощника…'}
          placeholderTextColor={colors.textMuted}
          editable={!locked}
          multiline
          onSubmitEditing={() => ask()}
        />
        {SpeechRec && !locked ? (
          <Pressable style={[styles.micBtn, listening && styles.micOn]} onPress={dictate}>
            <Text style={styles.micText}>{listening ? '■' : '🎤'}</Text>
          </Pressable>
        ) : null}
        <Pressable style={[styles.sendBtn, (!input.trim() || busy || locked) && styles.sendOff]} onPress={() => ask()} disabled={!input.trim() || busy || locked}>
          <Text style={styles.sendText}>➤</Text>
        </Pressable>
      </View>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  topbar: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, padding: spacing.sm, backgroundColor: colors.surface, borderBottomWidth: 1, borderBottomColor: colors.border },
  segment: { flex: 1, flexDirection: 'row', gap: spacing.xs },
  seg: { flex: 1, paddingVertical: 8, borderRadius: radius.sm, alignItems: 'center', backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border },
  segOn: { borderColor: colors.primary, backgroundColor: 'rgba(237,42,35,0.08)' },
  segText: { ...typography.caption, color: colors.textSecondary, fontWeight: '800' },
  segTextOn: { color: colors.primary },
  clear: { ...typography.caption, color: colors.textMuted, fontWeight: '700' },
  feed: { padding: spacing.md, gap: spacing.sm, paddingBottom: spacing.lg },
  hello: { flexDirection: 'row', gap: spacing.sm, alignItems: 'flex-start', backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border, borderRadius: radius.md, padding: spacing.md, marginBottom: spacing.xs },
  helloText: { ...typography.caption, color: colors.textSecondary, flex: 1 },
  chips: { gap: spacing.xs, marginBottom: spacing.sm, alignSelf: 'stretch' },
  chip: { backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border, borderRadius: radius.md, paddingHorizontal: spacing.md, paddingVertical: spacing.sm + 1 },
  chipText: { ...typography.caption, color: colors.textPrimary },
  bubble: {
    maxWidth: '86%',
    borderRadius: radius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + 2,
  },
  bubbleMine: { alignSelf: 'flex-end', backgroundColor: colors.primary, borderTopRightRadius: 6 },
  bubbleAi: { alignSelf: 'flex-start', backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border, borderTopLeftRadius: 6 },
  msg: { ...typography.body, color: colors.textPrimary, fontSize: 14.5, lineHeight: 21, flexShrink: 1 },
  msgMine: { color: colors.onPrimary },
  thinking: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  thinkingText: { ...typography.caption, color: colors.textMuted },
  err: { ...typography.caption, color: colors.danger, paddingHorizontal: spacing.sm, paddingTop: spacing.xs },
  composer: { flexDirection: 'row', alignItems: 'flex-end', gap: spacing.sm, padding: spacing.sm, borderTopWidth: 1, borderTopColor: colors.border, backgroundColor: colors.surface },
  input: { flex: 1, maxHeight: 120, backgroundColor: colors.inputBackground, borderWidth: 1, borderColor: colors.border, borderRadius: radius.md, paddingHorizontal: spacing.md, paddingVertical: spacing.sm + 2, color: colors.textPrimary, fontFamily: typography.body.fontFamily, fontSize: 15 },
  micBtn: { width: 42, height: 42, borderRadius: 21, backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border, alignItems: 'center', justifyContent: 'center' },
  micOn: { backgroundColor: 'rgba(237,42,35,0.12)', borderColor: colors.primary },
  micText: { fontSize: 16 },
  sendBtn: { width: 42, height: 42, borderRadius: 21, backgroundColor: colors.primary, alignItems: 'center', justifyContent: 'center' },
  sendOff: { opacity: 0.4 },
  sendText: { color: colors.onPrimary, fontSize: 16, fontWeight: '900' },
});
